/** Invoice list with status tabs and filters. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type InvoiceResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { apiGetPage } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import {
  DataList,
  FilterTabs,
  SearchBox,
  useUrlFilters,
} from "../../components/ui/controls.js";
import {
  ErrorPanel,
  ExportCsvLink,
  LoadingPanel,
  formatDate,
  formatMoney,
} from "../shared.js";
import { PaymentBadge, invoiceLabel } from "./invoice-common.js";

export const TABS = [
  "",
  "draft",
  "open",
  "overdue",
  "paid",
  "credit_note",
  "cancelled",
] as const;

export type Tab = (typeof TABS)[number];

export function tabQuery(tab: Tab) {
  switch (tab) {
    case "draft":
      return { status: "draft" };
    case "open":
      return { open: "true" };
    case "overdue":
      return { overdue: "true" };
    case "paid":
      return { kind: "invoice", paymentStatus: "paid" };
    case "credit_note":
      return { kind: "credit_note" };
    case "cancelled":
      return { status: "cancelled" };
    default:
      return {};
  }
}

export function InvoicesListPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const { values, page, set } = useUrlFilters(["tab", "q"] as const);
  const tab = (values.tab || "") as Tab;
  const pageSize = 25;
  const query = useQuery({
    queryKey: ["invoices", values, page],
    queryFn: () =>
      apiGetPage<InvoiceResponse>("/invoices", {
        ...tabQuery(tab),
        q: values.q || undefined,
        page,
        pageSize,
      }),
    placeholderData: (previous) => previous,
  });
  return (
    <>
      <PageHeader
        title={t("invoice.plural")}
        subtitle={t("invoice.listSubtitle")}
        actions={
          <>
            <ExportCsvLink dataset="invoices" query={{ q: values.q }} />
            {has("invoices.create") ? (
              <Button as={Link} to="/invoices/new" icon="pi pi-plus">
                {t("invoice.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterTabs
          value={tab}
          onChange={(next) => set({ tab: next })}
          options={TABS.map((value) => ({
            value,
            label: value ? t(`invoice.tabs.${value}`) : t("common.all"),
          }))}
        />
        <SearchBox value={values.q} onChange={(q) => set({ q })} />
      </div>
      {query.error ? (
        <ErrorPanel error={query.error} />
      ) : !query.data ? (
        <LoadingPanel />
      ) : (
        <DataList
          rows={query.data.data}
          rowKey={(invoice) => invoice.id}
          href={(invoice) => `/invoices/${invoice.id}`}
          page={page}
          pageSize={pageSize}
          total={query.data.total}
          onPage={(next) => set({ page: next })}
          loading={query.isFetching || undefined}
          empty={values.q || tab ? t("common.noMatches") : t("invoice.empty")}
          columns={[
            {
              key: "number",
              header: t("common.number"),
              cell: (invoice) => invoiceLabel(invoice, t),
            },
            {
              key: "customer",
              header: t("common.customer"),
              cell: (invoice) =>
                invoice.billing.company || invoice.billing.name,
            },
            {
              key: "title",
              header: t("common.title"),
              cell: (invoice) => invoice.title ?? "",
              hideOnMobile: true,
            },
            {
              key: "due",
              header: t("invoice.dueDate"),
              cell: (invoice) => formatDate(invoice.dueDate),
              hideOnMobile: true,
            },
            {
              key: "total",
              header: t("totals.total"),
              align: "right",
              cell: (invoice) =>
                `${invoice.kind === "credit_note" ? "−" : ""}${formatMoney(invoice.grandTotal, invoice.currency)}`,
            },
            {
              key: "state",
              header: t("common.status"),
              cell: (invoice) => <PaymentBadge invoice={invoice} />,
            },
          ]}
        />
      )}
    </>
  );
}
